interface AudioValidationResult {
  duration: number;
  size: number;
  isValid: boolean;
  warning?: string;
}

/**
 * Validates an audio file by decoding it in the browser to check actual duration and size.
 * Uses Web Audio API to decode and measure the audio.
 */
export async function validateAudioFile(blob: Blob): Promise<AudioValidationResult> {
  const size = blob.size;

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    try {
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const duration = audioBuffer.duration;

      await audioContext.close();

      // Check for suspiciously short audio
      if (duration < 1) {
        return {
          duration,
          size,
          isValid: false,
          warning: `Audio file appears to be only ${duration.toFixed(2)} seconds long. The file may be corrupted or empty.`,
        };
      }

      // Check if file size is disproportionately small for the duration
      // Compressed audio is typically at least 1KB per second (8kbps)
      const minExpectedSize = duration * 1000; // 1KB per second minimum
      if (size < minExpectedSize && duration > 10) {
        return {
          duration,
          size,
          isValid: true,
          warning: `Audio file size (${formatBytes(size)}) seems small for ${formatDuration(duration)} of audio. The file may be partially corrupted.`,
        };
      }

      console.log(`Audio validation passed: duration=${formatDuration(duration)}, size=${formatBytes(size)}`);
      return { duration, size, isValid: true };
    } catch (decodeError) {
      await audioContext.close();
      // Some audio formats (e.g., certain m4a encodings) may not be decodable by Web Audio API
      console.warn("Could not decode audio file for validation - proceeding with upload:", decodeError);
      return {
        duration: -1,
        size,
        isValid: true,
        warning: "Could not verify audio duration. The file will still be uploaded.",
      };
    }
  } catch (error) {
    console.warn("Audio validation failed - proceeding with upload:", error);
    return {
      duration: -1,
      size,
      isValid: true,
      warning: "Could not verify audio file. The file will still be uploaded.",
    };
  }
}

export function formatDuration(seconds: number): string {
  if (seconds < 0) return "unknown";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : `${secs}s`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
